<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { message, Modal } from 'ant-design-vue';
import {
    getRegistrations,
    exportRegistrations,
    getRegistrationDetail,
    updateFormSubmissionCompletion,
    getFormActivities,
    getFormSessions,
    getFormSubmissionLogs,
    getFormSubmission,
    getFormQuotaCategories
} from '@/api/forms';
import { fetchAllDepartmentRows } from '@/api/departments';
import { getUserInventoryLogs } from '@/api/user-inventory';
import {
    buildInventoryLogDiff,
    buildSubmissionLogDiff,
    formatLogEvent
} from '@/utils/logFormatter';
import DataTable from '@/components/common/DataTable.vue';
import FormCompletionConfirmModal from '@/components/forms/FormCompletionConfirmModal.vue';
import formatDateTime from '@/utils/formatDateTime';
import { formatNumber } from '@/utils/formatNumber';
import { fetchAllOptionRows } from '@/utils/safePaginatedFetch';

const props = defineProps({
    embedded: {
        type: Boolean,
        default: false
    },
    userId: {
        type: [Number, String],
        default: null
    },
    lockActivityId: {
        type: [Number, String],
        default: null
    },
    /** 表單詳情內嵌：鎖定只看該表單報名，避免同活動其他表單混入 */
    lockFormId: {
        type: [Number, String],
        default: null
    },
    initialSessionIds: {
        type: Array,
        default: () => []
    }
});

// --- 常量 ---
const COMPLETION_STATUS_OPTIONS = [
    { label: '全部', value: 'all' },
    { label: '準備上課', value: 'none' },
    { label: '已完課', value: 'completed' },
    { label: '未完課', value: 'absent' },
    { label: '已取消', value: 'cancelled' }
];

const COMPLETION_STATUS_MAP = {
    none: { label: '準備上課', color: 'processing' },
    completed: { label: '已完課', color: 'success' },
    absent: { label: '未完課', color: 'error' },
    cancellation_pending: { label: '取消申請中', color: 'warning' },
    cancelled: { label: '已取消', color: 'error' },
    enrollment_pending: { label: '志工待審核', color: 'warning' }
};

const completionDisplay = (record) => {
    if (record?.enrollment_review_status === 'pending') {
        return COMPLETION_STATUS_MAP.enrollment_pending;
    }
    if (record?.enrollment_review_status === 'rejected') {
        return COMPLETION_STATUS_MAP.cancelled;
    }
    return (
        COMPLETION_STATUS_MAP[record?.completion_status] || {
            label: record?.completion_status_label || '-',
            color: 'default'
        }
    );
};

const ITEM_STATUS_MAP = {
    available: { label: '可使用', color: 'success' },
    used: { label: '使用中', color: 'processing' },
    course_completed: { label: '已完課', color: 'default' },
    expired: { label: '過期', color: 'default' },
    disabled: { label: '失效', color: 'error' }
};

const ORDER_STATUS_MAP = {
    pending: { label: '待付款', color: 'orange' },
    paid: { label: '已付款', color: 'green' },
    completed: { label: '已完成', color: 'blue' },
    cancelled: { label: '已取消', color: 'gray' },
    cancellation_pending: { label: '取消審核中', color: 'red' },
    refunding: { label: '退款中', color: 'orange' },
    partially_refunded: { label: '部分退款', color: 'orange' },
    refunded: { label: '已退款', color: 'gray' },
    refund_failed: { label: '退款失敗', color: 'red' }
};

// --- 狀態 ---
const loading = ref(false);
const exportLoading = ref(false);
const listData = ref([]);
const pagination = reactive({
    current: 1,
    pageSize: 100,
    total: 0
});

// 選擇項
const selectedRowKeys = ref([]);
const onSelectChange = (keys) => {
    selectedRowKeys.value = keys;
};

const rowSelection = computed(() => ({
    selectedRowKeys: selectedRowKeys.value,
    onChange: onSelectChange
}));

/** 會員詳情內嵌：較寬欄位＋較大間距；獨立頁維持原欄寬 */
const embeddedFilterGutter = computed(() =>
    props.embedded ? [20, 20] : [16, 16]
);

const filterCol = computed(() => {
    if (props.embedded) {
        return {
            activity: { xs: 24, sm: 12, md: 12, lg: 12 },
            session: { xs: 24, sm: 12, md: 12, lg: 12 },
            department: { xs: 24, sm: 12, md: 8, lg: 8 },
            quota: { xs: 24, sm: 12, md: 8, lg: 8 },
            status: { xs: 24, sm: 12, md: 8, lg: 8 },
            query: { xs: 24, sm: 24, md: 24, lg: 24 }
        };
    }
    return {
        activity: { xs: 24, sm: 12, md: 6, lg: 5 },
        session: { xs: 24, sm: 12, md: 6, lg: 5 },
        department: { xs: 24, sm: 12, md: 6, lg: 4 },
        quota: { xs: 24, sm: 12, md: 6, lg: 4 },
        status: { xs: 24, sm: 12, md: 6, lg: 4 },
        query: { xs: 24, sm: 12, md: 8, lg: 7 }
    };
});

const filters = reactive({
    form_activity_id: undefined,
    'form_session_ids[]': [],
    department_id: undefined,
    quota_category_id: undefined,
    keyword: '',
    completion_status: 'all',
    sort_by: 'department_enrolled',
    sort_order: 'asc'
});

const scopedToMember = computed(() => !!props.userId);
const lockedFormId = computed(() =>
    props.lockFormId != null && props.lockFormId !== ''
        ? Number(props.lockFormId)
        : null
);
const hasListScope = computed(
    () =>
        !!filters.form_activity_id ||
        !!props.userId ||
        lockedFormId.value != null
);
const activityPlaceholder = computed(() =>
    scopedToMember.value ? '選擇活動（選填）' : '選擇活動 (必填)'
);
/** 表單詳情已鎖 form_id，不再顯示／切換活動 */
const showActivityFilter = computed(() => lockedFormId.value == null);
const showSessionFilter = computed(
    () => showActivityFilter.value || !!props.lockActivityId
);

function buildListParams(extra = {}) {
    const params = {
        ...filters,
        user_id: props.userId || undefined,
        form_id: lockedFormId.value ?? undefined,
        form_session_ids: filters['form_session_ids[]']?.length
            ? filters['form_session_ids[]']
            : undefined,
        ...extra
    };
    delete params['form_session_ids[]'];
    return params;
}

// 選單資料
const activityOptions = ref([]);
const sessionOptions = ref([]);
const departmentOptions = ref([]);
const quotaCategoryOptions = ref([]);

// 詳情 Modal
const detailVisible = ref(false);
const detailLoading = ref(false);
const currentDetail = ref(null);

// 編輯 Modal（場次／完課狀態）
const editVisible = ref(false);
const editLoading = ref(false);
const editSessionOptions = ref([]);
const editForm = reactive({
    submission_id: null,
    session_id: null,
    completion_status: 'none',
    is_completed: false
});

// --- 答案解析輔助 (由 FormSubmissions 引入以保持一致) ---
const tagColors = [
    '#2a4a7a',
    '#64748b',
    '#3b6ea5',
    '#64d5ff',
    '#ffd166',
    '#a0c4ff',
    '#ffb4d6',
    '#95e1d3'
];
const getTagColor = (idx) => tagColors[idx % tagColors.length];

const getParsedAnswerValue = (value) => {
    if (Array.isArray(value)) return value;
    if (
        typeof value === 'string' &&
        (value.startsWith('[') || value.startsWith('{'))
    ) {
        try {
            return JSON.parse(value);
        } catch {
            /* ignore */
        }
    }
    return value;
};

const getOptionsArray = (answer) => {
    const toStr = (o) => {
        if (typeof o === 'string') return o.trim();
        if (o && typeof o === 'object')
            return String(o.label ?? o.text ?? o.name ?? o.value ?? '').trim();
        return String(o ?? '').trim();
    };
    const normalize = (arr) =>
        Array.isArray(arr) ? arr.map(toStr).filter(Boolean) : [];

    const snapshot = answer.topic_options_snapshot;
    if (snapshot) {
        if (typeof snapshot === 'string') {
            try {
                const parsed = JSON.parse(snapshot);
                return normalize(Array.isArray(parsed) ? parsed : [parsed]);
            } catch {
                return snapshot
                    .split(',')
                    .map((s) => s.trim().replace(/["\[\]\\]/g, ''))
                    .filter(Boolean);
            }
        }
        if (Array.isArray(snapshot)) return normalize(snapshot);
    }
    const options = answer.options || answer.option_items || [];
    if (typeof options === 'string')
        return options
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    return normalize(options);
};

const isOptionSelected = (answerValue, option) => {
    const normalize = (v) =>
        typeof v === 'string' ? v.trim() : String(v ?? '');
    const optStr = normalize(option);
    const parsedVal = getParsedAnswerValue(answerValue);
    if (Array.isArray(parsedVal))
        return parsedVal.some((v) => normalize(v) === optStr);
    return normalize(parsedVal) === optStr;
};

// 操作紀錄 Drawer
const logsDrawerVisible = ref(false);
const logsLoading = ref(false);
const logsList = ref([]);
const logsTitle = ref('');
const logsType = ref(''); // 'inventory', 'submission'

const openInventoryLogs = async (record) => {
    if (!record.item?.id) return;
    logsType.value = 'inventory';
    logsTitle.value = `物品操作紀錄 - ${record.user?.name || '-'}`;
    logsDrawerVisible.value = true;
    logsLoading.value = true;
    logsList.value = [];
    try {
        const res = await getUserInventoryLogs(record.item.id);
        logsList.value = res.data || [];
    } catch {
        message.error('載入物品紀錄失敗');
    } finally {
        logsLoading.value = false;
    }
};

const openSubmissionLogs = async (record) => {
    logsType.value = 'submission';
    logsTitle.value = `表單提交紀錄 - ${record.user?.name || '-'}`;
    logsDrawerVisible.value = true;
    logsLoading.value = true;
    logsList.value = [];
    try {
        const res = await getFormSubmissionLogs(record.submission_id);
        logsList.value = res.data || [];
    } catch {
        message.error('載入表單紀錄失敗');
    } finally {
        logsLoading.value = false;
    }
};

// 表頭 key 對應後端 sort_by。欄位沒有 dataIndex 時 sorter.field 是空的，要看 columnKey。
const SORT_FIELD_MAP = {
    department: 'department_enrolled',
    user: 'name',
    session: 'session',
    completion_status: 'completion_status',
    enrolled_at: 'enrolled_at'
};

const columns = computed(() =>
    [
        { title: '事業部', key: 'department', dataIndex: 'department', width: 120, sorter: true },
        { title: '學員姓名', key: 'user', dataIndex: 'user', width: 150, sorter: true },
        { title: '身分 (配額)', key: 'quota_category', width: 130 },
        { title: '報名場次', key: 'session', dataIndex: 'session', width: 180, sorter: true },
        { title: '物品狀態', key: 'item_status', width: 100 },
        {
            title: '完課狀態',
            key: 'completion_status',
            dataIndex: 'completion_status',
            width: 120,
            sorter: true
        },
        {
            title: '報名時間',
            key: 'enrolled_at',
            dataIndex: 'enrolled_at',
            width: 160,
            sorter: true
        },
        { title: '操作', key: 'actions', width: 200, fixed: 'right' }
    ].map((col) => {
        const apiField = SORT_FIELD_MAP[col.key];
        if (!apiField) return col;
        const active = filters.sort_by === apiField;
        return {
            ...col,
            sortOrder: active
                ? filters.sort_order === 'asc'
                    ? 'ascend'
                    : 'descend'
                : null
        };
    })
);

// 二次確認 Modal 狀態
const confirmModalOpen = ref(false);
const confirmModalLoading = ref(false);
const confirmTargetStatus = ref('');
const confirmTargetStatusLabel = ref('');
const confirmItems = ref([]);
const confirmSessionName = ref('');
const confirmActivityName = ref('');
const pendingConfirmAction = ref(null);

// 批量更新完課狀態（帶二次確認視窗）
const handleBatchUpdateStatus = (status, label) => {
    if (selectedRowKeys.value.length === 0) {
        message.warning('請先選擇要操作的項目');
        return;
    }

    const selectedRecords = listData.value.filter(
        (item) =>
            selectedRowKeys.value.includes(item.submission_id) ||
            selectedRowKeys.value.includes(item.id)
    );

    confirmItems.value = selectedRecords.map((r) => ({
        id: r.submission_id || r.id,
        userName: r.user?.name || r.user_name || '學員',
        userId: r.user_id || r.user?.id,
        currentStatus: r.completion_status,
        sessionName: r.session_name || r.session?.name || ''
    }));

    confirmTargetStatus.value = status;
    confirmTargetStatusLabel.value = label;

    const actOpt = activityOptions.value.find(
        (a) => a.value === filters.form_activity_id
    );
    confirmActivityName.value =
        actOpt?.label || selectedRecords[0]?.activity_name || '';

    const uniqueSessions = [
        ...new Set(
            selectedRecords
                .map((r) => r.session_name || r.session?.name)
                .filter(Boolean)
        )
    ];
    confirmSessionName.value =
        uniqueSessions.length === 1
            ? uniqueSessions[0]
            : uniqueSessions.length > 1
              ? '跨場次'
              : selectedRecords[0]?.session_name || '';

    pendingConfirmAction.value = async () => {
        confirmModalLoading.value = true;
        try {
            const promises = selectedRowKeys.value.map((id) =>
                updateFormSubmissionCompletion(id, {
                    completion_status: status
                })
            );
            await Promise.all(promises);

            message.success('批量更新成功');
            selectedRowKeys.value = [];
            confirmModalOpen.value = false;
            loadList();
        } catch (err) {
            message.error('部分項目更新失敗');
        } finally {
            confirmModalLoading.value = false;
        }
    };

    confirmModalOpen.value = true;
};

// 取得基礎選單資料
const fetchOptions = async () => {
    try {
        const [activities, departments, quotaRes] = await Promise.all([
            fetchAllOptionRows((params) => getFormActivities(params)),
            fetchAllDepartmentRows(),
            getFormQuotaCategories()
        ]);

        activityOptions.value = activities.map((item) => ({
            label: item.name,
            value: item.id
        }));

        departmentOptions.value = (departments || []).map((item) => ({
            label: item.name,
            value: item.id
        }));

        const quotaList = Array.isArray(quotaRes)
            ? quotaRes
            : quotaRes.data?.data || quotaRes.data || [];
        quotaCategoryOptions.value = quotaList.map((item) => ({
            label: item.name,
            value: item.id
        }));
    } catch (err) {
        console.error('Fetch options failed:', err);
    }
};

// 活動變更時載入場次
const onActivityChange = async (val) => {
    filters['form_session_ids[]'] = [];
    sessionOptions.value = [];
    if (!val) return;

    try {
        const res = await getFormSessions(val);
        const sessionList = Array.isArray(res)
            ? res
            : res.data?.data || res.data || [];

        const isLegacyOrEnded = (item) => {
            const name = String(item.name || '');
            if (name.startsWith('舊生') || name.includes('舊生｜')) return true;
            if (item.end_at) {
                const end = new Date(item.end_at);
                if (!Number.isNaN(end.getTime()) && end < new Date()) return true;
            }
            return false;
        };

        sessionOptions.value = sessionList
            .slice()
            .sort((a, b) => Number(isLegacyOrEnded(a)) - Number(isLegacyOrEnded(b)))
            .map((item) => ({
                label: isLegacyOrEnded(item)
                    ? `（歷史）${item.name}`
                    : item.name,
                value: item.id
            }));
    } catch (err) {
        console.error('Fetch sessions failed:', err);
    }
};

// 載入列表
const loadList = async () => {
    if (!hasListScope.value) {
        if (!props.embedded) {
            message.warning('請先選擇活動');
        }
        return;
    }

    loading.value = true;
    try {
        const res = await getRegistrations(
            buildListParams({
                page: pagination.current,
                per_page: pagination.pageSize
            })
        );
        if (res.result) {
            listData.value = res.data.data;
            pagination.total = res.data.pagination.total;
        }
    } catch (err) {
        message.error(err?.response?.data?.message || '載入失敗');
    } finally {
        loading.value = false;
    }
};

const handleSearch = () => {
    pagination.current = 1;
    loadList();
};

const handleReset = () => {
    Object.assign(filters, {
        form_activity_id: props.lockActivityId
            ? Number(props.lockActivityId)
            : undefined,
        'form_session_ids[]': props.initialSessionIds?.length
            ? [...props.initialSessionIds]
            : [],
        department_id: undefined,
        quota_category_id: undefined,
        keyword: '',
        completion_status: 'all',
        sort_by: 'department_enrolled',
        sort_order: 'asc'
    });
    if (!filters.form_activity_id) {
        sessionOptions.value = [];
    }
    pagination.current = 1;
    listData.value = [];
    loadList();
};

const handleTableChange = ({ pagination: paginationData, sorter }) => {
    if (paginationData?.current) {
        pagination.current = paginationData.current;
        pagination.pageSize = paginationData.pageSize;
    }

    const active = Array.isArray(sorter) ? sorter[0] : sorter;
    const columnKey =
        active?.columnKey || active?.column?.key || active?.field;
    const apiField = SORT_FIELD_MAP[columnKey];
    if (apiField && active?.order) {
        filters.sort_by = apiField;
        filters.sort_order = active.order === 'ascend' ? 'asc' : 'desc';
    } else if (apiField) {
        filters.sort_by = 'department_enrolled';
        filters.sort_order = 'asc';
    }

    loadList();
};

const canExportWholeSession = computed(
    () =>
        props.embedded &&
        !!props.lockActivityId &&
        !scopedToMember.value
);

const wholeSessionExportParams = () => {
    let sessionIds = filters['form_session_ids[]']?.length
        ? [...filters['form_session_ids[]']]
        : [];
    if (!sessionIds.length && sessionOptions.value.length === 1) {
        sessionIds = [sessionOptions.value[0].value];
    }
    return {
        form_activity_id: Number(props.lockActivityId),
        form_session_ids: sessionIds.length ? sessionIds : undefined,
        department_id: filters.department_id,
        quota_category_id: filters.quota_category_id,
        keyword: filters.keyword,
        completion_status: filters.completion_status
    };
};

const downloadRegistrationBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// 匯出 Excel（目前畫面：單張表單）
const handleExport = async () => {
    if (!hasListScope.value) {
        message.warning('請先選擇活動');
        return;
    }

    exportLoading.value = true;
    try {
        const blob = await exportRegistrations(buildListParams());
        if (blob?.type?.includes('application/json')) {
            const text = await blob.text();
            const json = JSON.parse(text);
            message.error(json.message || '匯出失敗');
            return;
        }
        downloadRegistrationBlob(
            blob,
            `報名名單_${formatDateTime(new Date(), 'YYYYMMDD_HHmmss')}.xlsx`
        );
    } catch (err) {
        message.error('匯出失敗');
    } finally {
        exportLoading.value = false;
    }
};

const handleExportWholeSession = async () => {
    if (!canExportWholeSession.value) return;
    const selected = filters['form_session_ids[]']?.length
        ? filters['form_session_ids[]'].length
        : 0;
    if (!selected && sessionOptions.value.length > 1) {
        message.warning('請先選擇場次，才會一次匯出該場次全部配額表單');
        return;
    }

    exportLoading.value = true;
    try {
        const blob = await exportRegistrations(wholeSessionExportParams());
        if (blob?.type?.includes('application/json')) {
            const text = await blob.text();
            const json = JSON.parse(text);
            message.error(json.message || '匯出失敗');
            return;
        }
        downloadRegistrationBlob(
            blob,
            `報名名單_整場次_${formatDateTime(new Date(), 'YYYYMMDD_HHmmss')}.xlsx`
        );
    } catch (err) {
        message.error('匯出失敗');
    } finally {
        exportLoading.value = false;
    }
};

// 查看詳情
const openDetail = async (record) => {
    currentDetail.value = null;
    detailVisible.value = true;
    detailLoading.value = true;
    try {
        const res = await getRegistrationDetail(record.submission_id);
        if (res.result) {
            currentDetail.value = res.data;

            if (
                !currentDetail.value.answers ||
                currentDetail.value.answers.length === 0
            ) {
                const subRes = await getFormSubmission(record.submission_id);
                const answers = subRes.data?.answers || subRes.answers || [];
                if (answers.length > 0) {
                    currentDetail.value.answers = answers;
                }
            }
        }
    } catch (err) {
        message.error('取得詳情失敗');
    } finally {
        detailLoading.value = false;
    }
};

const editRecord = ref(null);

const openEdit = async (record) => {
    editRecord.value = record;
    editForm.submission_id = record.submission_id;
    editForm.session_id = null;
    editForm.completion_status = 'none';
    editForm.is_completed = false;
    editSessionOptions.value = [];
    editVisible.value = true;
    editLoading.value = true;
    try {
        const res = await getRegistrationDetail(record.submission_id);
        if (res.result) {
            editForm.session_id = res.data.session?.id || null;
            editForm.completion_status = res.data.completion_status || 'none';
            editForm.is_completed = res.data.completion_status === 'completed';

            const activityId =
                res.data.form_activity_id ||
                res.data.activity_id ||
                record.form_activity_id ||
                filters.form_activity_id;
            if (activityId) {
                const sessionRes = await getFormSessions(activityId);
                const sessionList = Array.isArray(sessionRes)
                    ? sessionRes
                    : sessionRes.data?.data || sessionRes.data || [];

                editSessionOptions.value = sessionList.map((s) => ({
                    label: s.name,
                    value: s.id
                }));
            }
        }
    } catch (err) {
        message.error('取得報名資訊失敗');
        editVisible.value = false;
    } finally {
        editLoading.value = false;
    }
};

// 更新報名狀態（含完課狀態與場次調整，帶二次確認視窗）
const updateRegistration = (id) => {
    const status = editForm.completion_status;
    const labelMap = {
        none: '準備上課',
        completed: '已完課',
        absent: '未完課',
        cancelled: '已取消'
    };
    const label = labelMap[status] || status;

    const record =
        listData.value.find(
            (item) => item.id === id || item.submission_id === id
        ) || editRecord.value;

    const matchedSession = editSessionOptions.value.find(
        (s) => s.value === editForm.session_id
    );
    const sessionName =
        matchedSession?.label ||
        record?.session_name ||
        record?.session?.name ||
        '';

    const matchedActivity = activityOptions.value.find(
        (a) =>
            a.value ===
            (record?.form_activity_id ||
                record?.activity_id ||
                filters.form_activity_id)
    );
    const activityName = matchedActivity?.label || record?.activity_name || '';

    confirmItems.value = [
        {
            id: id,
            userName:
                record?.user?.name ||
                record?.user_name ||
                editForm.user_name ||
                '學員',
            userId: record?.user_id || record?.user?.id,
            currentStatus: record?.completion_status || 'none',
            sessionName: sessionName
        }
    ];

    confirmTargetStatus.value = status;
    confirmTargetStatusLabel.value = label;
    confirmSessionName.value = sessionName;
    confirmActivityName.value = activityName;

    const payload = {
        completion_status: editForm.completion_status,
        session_id: editForm.session_id
    };

    pendingConfirmAction.value = async () => {
        confirmModalLoading.value = true;
        try {
            const res = await updateFormSubmissionCompletion(id, payload);
            if (res.result) {
                message.success('更新成功');
                confirmModalOpen.value = false;
                editVisible.value = false;
                loadList();
            }
        } catch (err) {
            message.error(err?.response?.data?.message || '更新失敗');
        } finally {
            confirmModalLoading.value = false;
        }
    };

    confirmModalOpen.value = true;
};

const handleConfirmAction = async () => {
    if (typeof pendingConfirmAction.value === 'function') {
        await pendingConfirmAction.value();
    }
};

// 解析答案顯示
const getParsedValue = (answer) => {
    if (!answer.value) return '-';
    try {
        const parsed = JSON.parse(answer.value);
        if (Array.isArray(parsed)) return parsed.join('、');
        return parsed;
    } catch {
        return answer.value;
    }
};

onMounted(async () => {
    await fetchOptions();
    if (props.lockActivityId) {
        filters.form_activity_id = Number(props.lockActivityId);
        await onActivityChange(filters.form_activity_id);
        if (props.initialSessionIds?.length) {
            filters['form_session_ids[]'] = [...props.initialSessionIds];
        }
        loadList();
    } else if (lockedFormId.value != null || props.userId) {
        loadList();
    }
});

watch(
    () => props.userId,
    (id, prev) => {
        if (!id || id === prev) return;
        pagination.current = 1;
        loadList();
    }
);

watch(
    () => props.initialSessionIds,
    (ids) => {
        if (!props.embedded || !ids?.length) return;
        filters['form_session_ids[]'] = [...ids];
        if (filters.form_activity_id || props.userId) {
            loadList();
        }
    },
    { deep: true }
);
</script>

<template>
    <div
        :class="[
            'page',
            'points-page',
            'forms-page',
            'attendee-management',
            { 'attendee-management--embedded': embedded }
        ]"
    >
        <div v-if="!embedded" class="page-header">
            <h1>報名名單</h1>
            <p>管理活動報名、完課審核與名單導出</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 篩選列 -->
            <div class="toolbar toolbar-main toolbar-grid">
                <div class="filters-grid">
                    <a-row :gutter="embeddedFilterGutter">
                        <a-col v-if="showActivityFilter" v-bind="filterCol.activity">
                            <a-select
                                v-model:value="filters.form_activity_id"
                                :placeholder="activityPlaceholder"
                                class="status-select w-full"
                                size="middle"
                                show-search
                                allow-clear
                                option-filter-prop="label"
                                :options="activityOptions"
                                :disabled="!!lockActivityId"
                                @change="onActivityChange"
                            />
                        </a-col>
                        <a-col v-if="showSessionFilter" v-bind="filterCol.session">
                            <a-select
                                v-model:value="filters['form_session_ids[]']"
                                mode="multiple"
                                placeholder="選擇場次（可輸入關鍵字篩選）"
                                class="status-select w-full"
                                size="middle"
                                show-search
                                option-filter-prop="label"
                                :options="sessionOptions"
                                :disabled="!filters.form_activity_id"
                                max-tag-count="responsive"
                            />
                        </a-col>
                        <a-col v-bind="filterCol.department">
                            <a-select
                                v-model:value="filters.department_id"
                                placeholder="事業部"
                                class="status-select w-full"
                                size="middle"
                                allow-clear
                                :options="departmentOptions"
                            />
                        </a-col>
                        <a-col v-bind="filterCol.quota">
                            <a-select
                                v-model:value="filters.quota_category_id"
                                placeholder="身分 (配額)"
                                class="status-select w-full"
                                size="middle"
                                allow-clear
                                :options="quotaCategoryOptions"
                            />
                        </a-col>
                        <a-col v-bind="filterCol.status">
                            <a-select
                                v-model:value="filters.completion_status"
                                placeholder="完課狀態"
                                class="status-select w-full"
                                size="middle"
                                :options="COMPLETION_STATUS_OPTIONS"
                            />
                        </a-col>
                        <a-col v-bind="filterCol.query">
                            <div
                                class="toolbar-query-group toolbar-query-group--block"
                            >
                                <a-input
                                    v-if="!scopedToMember"
                                    v-model:value="filters.keyword"
                                    placeholder="搜尋學員姓名或手機"
                                    allow-clear
                                    size="middle"
                                    class="filter-input"
                                    @press-enter="handleSearch"
                                />
                                <a-button
                                    size="middle"
                                    class="unified-search-btn"
                                    @click="handleSearch"
                                >
                                    查詢
                                </a-button>
                                <a-button
                                    size="middle"
                                    class="unified-reset-btn"
                                    @click="handleReset"
                                >
                                    重置
                                </a-button>
                            </div>
                        </a-col>
                    </a-row>
                </div>
                <div class="toolbar-actions">
                    <a-space wrap :size="12">
                        <a-dropdown :disabled="selectedRowKeys.length === 0">
                            <a-button
                                size="middle"
                                class="unified-secondary-btn"
                            >
                                完課操作 ({{ selectedRowKeys.length }})
                            </a-button>
                            <template #overlay>
                                <a-menu>
                                    <a-menu-item
                                        @click="
                                            handleBatchUpdateStatus(
                                                'completed',
                                                '已完課'
                                            )
                                        "
                                    >
                                        勾選完課
                                    </a-menu-item>
                                    <a-menu-item
                                        @click="
                                            handleBatchUpdateStatus(
                                                'absent',
                                                '標記未完課'
                                            )
                                        "
                                    >
                                        標記未完課
                                    </a-menu-item>
                                    <a-menu-item
                                        @click="
                                            handleBatchUpdateStatus(
                                                'cancelled',
                                                '標記已取消'
                                            )
                                        "
                                    >
                                        標記已取消
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                        <a-button
                            size="middle"
                            class="unified-secondary-btn"
                            @click="handleExport"
                            :disabled="!hasListScope || exportLoading"
                            :loading="exportLoading"
                        >
                            匯出本表單
                        </a-button>
                        <a-tooltip
                            v-if="canExportWholeSession"
                            title="含本活動其他配額表單（例如複訓），同一場次一次匯出"
                        >
                            <a-button
                                size="middle"
                                class="unified-secondary-btn"
                                @click="handleExportWholeSession"
                                :disabled="exportLoading"
                                :loading="exportLoading"
                            >
                                匯出整場次
                            </a-button>
                        </a-tooltip>
                    </a-space>
                </div>
            </div>

            <!-- 數據表格 -->
            <DataTable
                row-key="submission_id"
                class="points-table"
                :columns="columns"
                :data-source="listData"
                :pagination="pagination"
                :loading="loading"
                :row-selection="rowSelection"
                @refresh="loadList"
                @change="handleTableChange"
            >
                <template #bodyCell="{ column, record, index, text }">
                    <!-- 序號列處理 -->
                    <template v-if="column.key === 'tableIndex'">
                        <span>
                            {{
                                (pagination.current - 1) * pagination.pageSize +
                                index +
                                1
                            }}
                        </span>
                    </template>

                    <template v-else-if="column.key === 'department'">
                        {{ record.user?.department?.name || '-' }}
                    </template>
                    <template v-else-if="column.key === 'user'">
                        <div class="font-medium">
                            {{ record.user?.name || '-' }}
                        </div>
                        <div class="text-xs text-gray-400">
                            {{ record.user?.phone || '-' }}
                        </div>
                    </template>
                    <template v-else-if="column.key === 'quota_category'">
                        <a-tag
                            v-if="record.quota_category?.name"
                            color="purple"
                        >
                            {{ record.quota_category.name }}
                        </a-tag>
                        <span v-else class="text-gray-400">-</span>
                    </template>
                    <template v-else-if="column.key === 'session'">
                        {{ record.session?.name || '-' }}
                    </template>
                    <template v-else-if="column.key === 'item_status'">
                        <a-tag
                            v-if="record.item"
                            :color="ITEM_STATUS_MAP[record.item.status]?.color"
                        >
                            {{ record.item.status_label }}
                        </a-tag>
                        <span v-else>-</span>
                    </template>
                    <template v-else-if="column.key === 'completion_status'">
                        <a-badge
                            :status="completionDisplay(record).color"
                            :text="
                                record.completion_status_label ||
                                completionDisplay(record).label
                            "
                        />
                    </template>
                    <template v-else-if="column.key === 'enrolled_at'">
                        {{ record.enrolled_at || '-' }}
                    </template>
                    <template v-else-if="column.key === 'actions'">
                        <a-space size="small" class="action-buttons">
                            <a-button
                                size="small"
                                class="action-btn-view"
                                @click="openDetail(record)"
                            >
                                詳情
                            </a-button>
                            <a-button
                                size="small"
                                class="action-btn-edit"
                                @click="openEdit(record)"
                            >
                                編輯
                            </a-button>
                            <a-button
                                v-if="record.item"
                                size="small"
                                class="action-btn-logs"
                                @click="openInventoryLogs(record)"
                            >
                                物品紀錄
                            </a-button>
                            <a-button
                                size="small"
                                class="action-btn-logs"
                                @click="openSubmissionLogs(record)"
                            >
                                表單紀錄
                            </a-button>
                        </a-space>
                    </template>
                    <!-- 預設顯示 -->
                    <template v-else>
                        {{ column.dataIndex ? text : '' }}
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 操作紀錄 Modal -->
        <a-modal
            v-model:open="logsDrawerVisible"
            :title="logsTitle"
            width="640px"
            :footer="null"
            centered
            destroy-on-close
            class="vaporwave-modal"
            @cancel="logsDrawerVisible = false"
        >
            <a-spin :spinning="logsLoading">
                <a-empty
                    v-if="!logsLoading && !logsList.length"
                    description="尚無操作紀錄"
                    class="mt-8"
                />
                <div v-else class="logs-list logs-list--modal">
                    <div
                        v-for="log in logsList"
                        :key="log.id"
                        class="log-entry mb-4 p-3 rounded bg-purple-50 border border-purple-100 shadow-sm"
                    >
                        <div
                            class="log-header flex justify-between items-center mb-2"
                        >
                            <span class="log-event font-bold text-purple-700">
                                {{
                                    formatLogEvent(
                                        log.event ||
                                            log.action_label ||
                                            log.action
                                    )
                                }}
                            </span>
                            <span class="log-time text-xs text-gray-400">
                                {{ formatDateTime(log.created_at) }}
                            </span>
                        </div>
                        <div class="log-causer text-xs text-gray-600 mb-2">
                            操作者：{{
                                log.causer_name ||
                                log.actor?.name ||
                                log.user?.name ||
                                '系統'
                            }}
                        </div>

                        <!-- Diff 內容 -->
                        <div
                            v-if="
                                logsType === 'inventory' ||
                                logsType === 'submission'
                            "
                            class="log-changes"
                        >
                            <div
                                v-for="diff in logsType === 'inventory'
                                    ? buildInventoryLogDiff(
                                          log.old_values ||
                                              log.changes?.old_values ||
                                              {},
                                          log.new_values ||
                                              log.changes?.new_values ||
                                              {}
                                      )
                                    : buildSubmissionLogDiff(
                                          log.old_values ||
                                              log.changes?.old_values ||
                                              {},
                                          log.new_values ||
                                              log.changes?.new_values ||
                                              {}
                                      )"
                                :key="diff.key"
                                class="log-diff-row"
                            >
                                <span class="log-diff-field">
                                    {{ diff.label }}
                                </span>
                                <span class="log-diff-sep">:</span>
                                <template v-if="diff.hasOld">
                                    <span class="log-diff-old">
                                        {{ diff.oldVal }}
                                    </span>
                                    <span class="log-diff-arrow">→</span>
                                </template>
                                <span class="log-diff-new">
                                    {{ diff.newVal }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </a-spin>
        </a-modal>

        <!-- 詳情 Modal -->
        <a-modal
            v-model:open="detailVisible"
            title="報名詳情紀錄"
            width="900px"
            :footer="null"
            centered
            class="vaporwave-modal"
        >
            <a-spin :spinning="detailLoading">
                <template v-if="currentDetail">
                    <div class="detail-container p-4">
                        <!-- 學員與課程摘要 -->
                        <div class="section mb-6">
                            <h3 class="section-title">學員與課程資訊</h3>
                            <a-descriptions bordered size="small" :column="2">
                                <a-descriptions-item label="姓名">
                                    {{ currentDetail.user?.name }}
                                </a-descriptions-item>
                                <a-descriptions-item label="手機">
                                    {{ currentDetail.user?.phone }}
                                </a-descriptions-item>
                                <a-descriptions-item label="事業部">
                                    {{ currentDetail.user?.department?.name }}
                                </a-descriptions-item>
                                <a-descriptions-item label="報名時間">
                                    {{ currentDetail.enrolled_at }}
                                </a-descriptions-item>
                                <a-descriptions-item label="課程名稱" :span="2">
                                    {{ currentDetail.form_name }}
                                </a-descriptions-item>
                                <a-descriptions-item label="場次名稱">
                                    {{ currentDetail.session?.name }}
                                </a-descriptions-item>
                                <a-descriptions-item label="上課地點">
                                    {{ currentDetail.session?.location || '-' }}
                                </a-descriptions-item>
                            </a-descriptions>
                        </div>

                        <a-tabs default-active-key="1">
                            <!-- 表單答案 -->
                            <a-tab-pane key="1" tab="報名表答案">
                                <div class="answers-list mt-4">
                                    <div
                                        v-for="(
                                            answer, idx
                                        ) in currentDetail.answers"
                                        :key="answer.field_id"
                                        class="answer-card mb-6 p-4 rounded-lg bg-gray-50 border border-gray-100"
                                    >
                                        <div
                                            class="flex justify-between items-center mb-3"
                                        >
                                            <h4
                                                class="text-gray-700 font-bold mb-0"
                                            >
                                                Q{{ idx + 1 }}.
                                                {{ answer.topic_name_snapshot }}
                                            </h4>
                                            <a-tag
                                                v-if="answer.topic_type"
                                                :color="getTagColor(idx)"
                                            >
                                                {{ answer.topic_type }}
                                            </a-tag>
                                        </div>

                                        <div
                                            class="answer-content pl-4 border-l-2 border-purple-200"
                                        >
                                            <!-- 多選/單選選項顯示 -->
                                            <template
                                                v-if="
                                                    getOptionsArray(answer)
                                                        .length > 0
                                                "
                                            >
                                                <div
                                                    class="flex flex-wrap gap-2"
                                                >
                                                    <a-tag
                                                        v-for="opt in getOptionsArray(
                                                            answer
                                                        )"
                                                        :key="opt"
                                                        :color="
                                                            isOptionSelected(
                                                                answer.value,
                                                                opt
                                                            )
                                                                ? 'purple'
                                                                : 'default'
                                                        "
                                                        :class="{
                                                            'selected-opt':
                                                                isOptionSelected(
                                                                    answer.value,
                                                                    opt
                                                                )
                                                        }"
                                                    >
                                                        {{ opt }}
                                                    </a-tag>
                                                </div>
                                            </template>
                                            <!-- 布林值 -->
                                            <template
                                                v-else-if="
                                                    answer.topic_type ===
                                                        'boolean' ||
                                                    typeof getParsedAnswerValue(
                                                        answer.value
                                                    ) === 'boolean'
                                                "
                                            >
                                                <a-badge
                                                    :status="
                                                        getParsedAnswerValue(
                                                            answer.value
                                                        )
                                                            ? 'success'
                                                            : 'default'
                                                    "
                                                    :text="
                                                        getParsedAnswerValue(
                                                            answer.value
                                                        )
                                                            ? '是 / 同意'
                                                            : '否 / 不同意'
                                                    "
                                                />
                                            </template>
                                            <!-- 純文字 -->
                                            <template v-else>
                                                <div
                                                    class="text-sm text-gray-600 whitespace-pre-wrap"
                                                >
                                                    {{
                                                        getParsedAnswerValue(
                                                            answer.value
                                                        ) || '─'
                                                    }}
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                    <a-empty
                                        v-if="!currentDetail.answers?.length"
                                        description="無報名資料"
                                    />
                                </div>
                            </a-tab-pane>

                            <!-- 物品與訂單 -->
                            <a-tab-pane key="2" tab="物品與訂單">
                                <div class="mt-4">
                                    <h4 class="font-bold mb-2">對應背包物品</h4>
                                    <a-descriptions
                                        bordered
                                        size="small"
                                        :column="2"
                                        v-if="currentDetail.item"
                                    >
                                        <a-descriptions-item label="物品名稱">
                                            {{ currentDetail.item.name }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="狀態">
                                            {{
                                                currentDetail.item.status_label
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="獲得時間">
                                            {{ currentDetail.item.acquired_at }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="過期時間">
                                            {{
                                                currentDetail.item.expires_at ||
                                                '無'
                                            }}
                                        </a-descriptions-item>
                                    </a-descriptions>
                                    <a-empty v-else description="無物品資料" />

                                    <h4 class="font-bold mt-6 mb-2">
                                        訂單摘要
                                    </h4>
                                    <a-descriptions
                                        bordered
                                        size="small"
                                        :column="2"
                                        v-if="currentDetail.order"
                                    >
                                        <a-descriptions-item label="訂單編號">
                                            {{ currentDetail.order.order_no }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="狀態">
                                            <a-tag
                                                :color="
                                                    ORDER_STATUS_MAP[
                                                        currentDetail.order
                                                            .status
                                                    ]?.color || 'default'
                                                "
                                            >
                                                {{
                                                    ORDER_STATUS_MAP[
                                                        currentDetail.order
                                                            .status
                                                    ]?.label ||
                                                    currentDetail.order.status
                                                }}
                                            </a-tag>
                                        </a-descriptions-item>
                                        <a-descriptions-item label="實付金額">
                                            ${{
                                                formatNumber(
                                                    currentDetail.order
                                                        .grand_total
                                                )
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="付款時間">
                                            {{ currentDetail.order.paid_at }}
                                        </a-descriptions-item>
                                    </a-descriptions>
                                    <a-empty v-else description="無訂單資料" />
                                </div>
                            </a-tab-pane>
                        </a-tabs>

                        <div
                            class="modal-actions mt-6 flex justify-end border-t pt-4"
                        >
                            <a-button
                                size="middle"
                                @click="detailVisible = false"
                            >
                                關閉
                            </a-button>
                        </div>
                    </div>
                </template>
            </a-spin>
        </a-modal>

        <!-- 編輯報名 Modal -->
        <a-modal
            v-model:open="editVisible"
            title="編輯報名資訊"
            width="480px"
            centered
            class="vaporwave-modal"
            :footer="null"
        >
            <a-spin :spinning="editLoading">
                <div class="edit-form-fields">
                    <div class="edit-form-row">
                        <span class="edit-form-label">調整場次</span>
                        <a-select
                            v-model:value="editForm.session_id"
                            placeholder="選擇場次"
                            class="edit-form-control"
                            size="middle"
                            :options="editSessionOptions"
                        />
                    </div>
                    <div class="edit-form-row">
                        <span class="edit-form-label">完課狀態</span>
                        <a-select
                            v-model:value="editForm.completion_status"
                            placeholder="選擇狀態"
                            class="edit-form-control"
                            size="middle"
                            :options="
                                COMPLETION_STATUS_OPTIONS.filter(
                                    (o) => o.value !== 'all'
                                )
                            "
                        />
                    </div>
                </div>
                <div class="edit-modal-footer">
                    <a-button @click="editVisible = false">取消</a-button>
                    <a-button
                        class="unified-search-btn"
                        @click="updateRegistration(editForm.submission_id)"
                    >
                        更新報名資訊
                    </a-button>
                </div>
            </a-spin>
        </a-modal>

        <!-- 完課狀態變更二次確認 Modal -->
        <FormCompletionConfirmModal
            v-model:open="confirmModalOpen"
            :loading="confirmModalLoading"
            :activity-name="confirmActivityName"
            :session-name="confirmSessionName"
            :target-status="confirmTargetStatus"
            :target-status-label="confirmTargetStatusLabel"
            :items="confirmItems"
            @confirm="handleConfirmAction"
        />
    </div>
</template>

<style scoped>
.section-title {
    font-size: 16px;
    font-weight: bold;
    color: #2a4a7a;
    margin-bottom: 12px;
    padding-left: 8px;
    border-left: 3px solid #64748b;
}

/* 操作紀錄 Diff 樣式 */
.log-entry {
    background: rgba(255, 255, 255, 0.6);
    transition: all 0.3s ease;
    border-radius: 8px;
    border: 1px solid rgba(42, 74, 122, 0.1);
}

.log-entry:hover {
    box-shadow: 0 4px 12px rgba(42, 74, 122, 0.15);
}

.log-event {
    font-weight: 600;
    color: #8b5aad;
    font-family: 'Courier New', monospace;
    font-size: 13px;
}

.logs-list--modal {
    max-height: min(60vh, 520px);
    overflow-y: auto;
    padding-right: 4px;
}

.log-changes {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
}

.log-diff-row {
    display: flex;
    align-items: center;
    font-size: 13px;
    flex-wrap: wrap;
    gap: 2px;
}

.log-diff-field {
    color: #888;
    font-size: 12px;
    min-width: 60px;
}

.log-diff-sep {
    color: #aaa;
}

.log-diff-old {
    color: #f87171;
    text-decoration: line-through;
    font-family: 'Courier New', monospace;
}

.log-diff-arrow {
    color: #aaa;
    padding: 0 2px;
}

.log-diff-new {
    color: #10b981;
    font-weight: 600;
    font-family: 'Courier New', monospace;
}

.edit-form-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px 0 4px;
}

.edit-form-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.edit-form-label {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
}

.edit-form-control {
    width: 100%;
}

.edit-form-hint {
    margin: 0;
    font-size: 12px;
    color: #9ca3af;
}

.edit-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(138, 175, 255, 0.15);
}

.attendee-management--embedded {
    padding: 0;
    min-height: auto;
}

.attendee-management--embedded .content.vaporwave-theme {
    padding: 0 !important;
    min-height: auto !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    overflow: visible;
}

.attendee-management--embedded .content.vaporwave-theme::before {
    display: none !important;
}

.attendee-management--embedded .toolbar.toolbar-main {
    margin-bottom: 20px;
    padding: 16px 18px;
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid rgba(42, 74, 122, 0.18);
    border-radius: 12px;
}

.attendee-management--embedded .toolbar.toolbar-grid .toolbar-actions {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid rgba(42, 74, 122, 0.12);
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.attendee-management--embedded .toolbar-actions :deep(.ant-space),
.attendee-management--embedded .toolbar-actions :deep(.ant-space-item) {
    display: inline-flex;
    align-items: center;
}

.attendee-management--embedded .toolbar-actions :deep(.unified-secondary-btn) {
    height: 36px;
    line-height: 34px;
    padding: 0 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.attendee-management--embedded .filters-grid .ant-row {
    row-gap: 16px !important;
}

.attendee-management--embedded .toolbar-query-group--block {
    gap: 12px;
}

.attendee-management--embedded
    .toolbar-query-group--block
    .filter-input.ant-input,
.attendee-management--embedded
    .toolbar-query-group--block
    .filter-input.ant-input-affix-wrapper {
    flex: 1 1 280px;
    min-width: 220px;
    max-width: 420px;
}
</style>
